import React, { useState, useCallback, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { debounce } from "lodash";

// Components
import { ListBoxXnuupPartIdComponent } from "./components/ListBoxXnuupPartIdComponent";
import { TextFieldXnuupPartIdComponent } from "./components/TextFieldXnuupPartIdComponent";

// Models
import { XnuupPartIdAutoCompleteResponse } from "@/main/models/response";
import { XnuupPartIdAutocompleteFilterRequest } from "@/main/models/request";

// Api's
import { partApi } from "@/main/api";

// Styles
import { useStyles } from "./style";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

// Utils & Enums
import {
	getDefaultMessageResultsFound,
	getTranslateByKey,
	isNullUndefined,
	removeEmptyValues,
} from "@/main/services/utils";
import { IGridData, XnuupPartIdAutoCompleteProps } from "./interfaces";
import { useSearchParams } from "react-router-dom";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { Spinner } from "../inputAutoComplete/styles";
import { KeyValue } from "@/main/models/common";

const SelectXnuupPartIdAutoCompleteComponent = React.forwardRef(
	(
		{
			watcherCategory,
			watcherSubcategory,
			partPIESName,
			handleChangeXnuupPartId,
			hasBorderStyle,
			hasClearValue,
			setHasClearValue,
			required = false,
			label,
			hasErrorCustom,
			errorMessageCustom,
			ignoreQueryParams = false,
			isCanastaBasica = true,
			showIdPIES = false,
			propsAutoComplete = {},
		}: XnuupPartIdAutoCompleteProps,
		ref: any,
	) => {
		const [searchParams] = useSearchParams();
		const partSearchParams = searchParams.get("partName");

		// Styles
		const classes = useStyles();

		// States
		const INITIAL_GRID_STATE: IGridData = {
			totalItems: 0,
			isLoading: false,
			data: [],
		};
		const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
		const [partIdSelected, setPartIdSelected] = useState<XnuupPartIdAutoCompleteResponse | null>(
			!isNullUndefined(partPIESName) ? ({ partPIESName: partPIESName } as XnuupPartIdAutoCompleteResponse) : null,
		);
		const [inputTextApi, setInputTextApi] = useState<string>("");
		const [inputText, setInputText] = useState<string>("");
		const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
		const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<boolean>(false);
		const [hasShowMore, setHasShowMore] = useState<boolean>(false);

		//#region Async Functions
		async function loadData(
			search: string = "",
			category: KeyValue | null = null,
			subcategory: KeyValue | null = null,
		) {
			let optionsData: any[] = [];

			setGridData({
				isLoading: true,
				totalItems: 0,
				data: [],
			});
			const index = PaginationEnum.PageIndexIntial;
			setPageIndex(index);
			const request: XnuupPartIdAutocompleteFilterRequest = {
				guidPartCategory: category ? String(category.key) : null,
				guidPartSubCategory: subcategory ? String(subcategory.key) : null,
				searchTerm: search,
				isCanastaBasica: isCanastaBasica,
				showIdPIES: showIdPIES,
				pageIndex: index,
				pageSize: PaginationEnum.PageSize10,
			};
			const requestFormatted = removeEmptyValues(request);
			const responseApi = await partApi.getXnuupPartId(requestFormatted);
			if (responseApi.data.results && responseApi.data.results.length > 0) {
				optionsData = responseApi.data.results;
			}
			setGridData({
				isLoading: false,
				totalItems: responseApi.data.totalNumberOfRecords,
				data: optionsData,
			});
			setHasShowMore(responseApi.data.pageNumber < responseApi.data.totalNumberOfPages);
		}
		//#endregion

		// Search term
		useEffect(() => {
			loadData(inputTextApi, watcherCategory, watcherSubcategory);
		}, [inputTextApi, watcherCategory?.key, watcherSubcategory?.key]);
		useEffect(() => {
			if (setHasClearValue && hasClearValue) {
				resetAllState();
				setPartIdSelected(null);
				setHasClearValue(false);
			}
		}, [hasClearValue]);
		useEffect(() => {
			if (partIdSelected && partPIESName && partPIESName !== "") {
				partIdSelected.partPIESName = partPIESName;
			} else {
				setPartIdSelected(
					partPIESName
						? ({
								partPIESName: partPIESName,
							} as XnuupPartIdAutoCompleteResponse)
						: null,
				);
			}
		}, [partPIESName]);

		useEffect(() => {
			if (!ignoreQueryParams && partSearchParams) {
				setInputTextApi(partSearchParams);
				setInputText(partSearchParams);
			}
		}, [partSearchParams]);

		const debounceOnChange = useCallback(
			debounce((valueTyping: string) => {
				setInputText(valueTyping);
				setInputTextApi(valueTyping);
			}, 600),
			[],
		);

		function getSearchTerm() {
			if (partIdSelected !== null && partIdSelected.partPIESName) {
				if (inputText !== "" && partIdSelected.partPIESName !== inputText) {
					return inputText;
				}

				return partIdSelected.partPIESName;
			}

			return inputText;
		}

		// Reset's
		function clearSearchTerms() {
			setInputText("");
			setInputTextApi("");
		}

		function resetAllState() {
			setGridData(INITIAL_GRID_STATE);
			setInputTextApi("");
			clearSearchTerms();
		}

		//#region Infinite scroll
		async function onLoadInfiniteScroll(request: XnuupPartIdAutocompleteFilterRequest) {
			setIsLoadingInfiniteScroll(true);
			partApi
				.getXnuupPartId(request)
				.then((response) => {
					let resultData: XnuupPartIdAutoCompleteResponse[] = [];
					let resultTotalItems: number = 0;
					if (response.data && response.data.results && response.data.results.length > 0) {
						resultData = response.data.results;
						resultTotalItems = response.data.totalNumberOfRecords;
					}
					if (gridData.data.length > 0) {
						resultData = [...gridData.data, ...resultData];
					}
					setGridData({
						isLoading: false,
						totalItems: resultTotalItems,
						data: resultData,
					});
					// Disable show more
					if (resultTotalItems < PaginationEnum.PageSize10 || resultTotalItems === resultData.length) {
						setHasShowMore(false);
					}
				})
				.finally(() => setIsLoadingInfiniteScroll(false));
		}

		async function onLoadSearchGrid() {
			const index = pageIndex + PaginationEnum.PageIndexIntial;
			setPageIndex(index);
			const request: XnuupPartIdAutocompleteFilterRequest = {
				guidPartCategory: watcherCategory ? String(watcherCategory.key) : null,
				guidPartSubCategory: watcherSubcategory ? String(watcherSubcategory.key) : null,
				isCanastaBasica: isCanastaBasica,
				showIdPIES: showIdPIES,
				searchTerm: inputTextApi,
				pageIndex: index,
				pageSize: PaginationEnum.PageSize10,
			};
			await onLoadInfiniteScroll(request);
		}

		const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
			const listboxNode = event.currentTarget;
			const position = listboxNode.scrollTop + listboxNode.clientHeight;
			if (listboxNode.scrollHeight - position <= 1 && hasShowMore && !isLoadingInfiniteScroll) {
				onLoadSearchGrid();
			}
		};
		//#endregion

		return (
			<div id="id-xnuupPartId-autocomplete-container">
				<Autocomplete
					popupIcon={gridData.isLoading ? <Spinner /> : <KeyboardArrowDown />}
					className={classes.inputAutoComplete}
					noOptionsText={getTranslateByKey("information.noRecordsFound")}
					id="id-xnuupPartId-autocomplete-fieldss"
					size="small"
					loading={gridData.isLoading}
					loadingText={`${getTranslateByKey("information.searchingXnuupPartId")}...`}
					value={partIdSelected}
					filterOptions={(filteredOptions: any[]) => filteredOptions}
					options={gridData.data}
					renderOption={(props, option: XnuupPartIdAutoCompleteResponse) => {
						return <li {...props}>{option.partPIESName}</li>;
					}}
					getOptionLabel={(option: string | XnuupPartIdAutoCompleteResponse) => {
						if (typeof option === "string") return option;
						return option ? option.partPIESName : "";
					}}
					onChange={(_: React.SyntheticEvent, partIdChange: string | XnuupPartIdAutoCompleteResponse | null) => {
						setPartIdSelected(partIdChange as XnuupPartIdAutoCompleteResponse | null);
						handleChangeXnuupPartId(partIdChange as XnuupPartIdAutoCompleteResponse);
					}}
					renderInput={(params) => (
						<TextFieldXnuupPartIdComponent
							params={params}
							required={required}
							propsCustom={{
								searchTerm: getSearchTerm(),
								label: label ? label : getTranslateByKey("information.xnuupPartId"),
								setInputText,
								hasBorderStyle,
							}}
							hasErrorCustom={hasErrorCustom}
							errorMessageCustom={errorMessageCustom}
						/>
					)}
					onInputChange={(_: React.SyntheticEvent, newInputValue: string, reason: string) => {
						const isEmpty = newInputValue === "";

						if (reason === "clear" || isEmpty) {
							resetAllState();
							loadData(newInputValue, watcherCategory, watcherSubcategory);
						} else if (reason === "reset") {
							clearSearchTerms();
						} else if (reason === "input") {
							debounceOnChange(newInputValue);
						}
					}}
					// Notation: Error on material for event type

					{...({
						ListboxProps: {
							onScroll: handleScroll,
							textResultFound: getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems),
							textIsLoading: `${getTranslateByKey("information.searchingXnuupPartId")}...`,
							isLoading: isLoadingInfiniteScroll,
						},
					} as any)}
					ListboxComponent={ListBoxXnuupPartIdComponent}
					disablePortal={true}
					ref={ref}
					{...propsAutoComplete}
				/>
			</div>
		);
	},
);

export default SelectXnuupPartIdAutoCompleteComponent;
